/**
 * User Theme Creator Component
 * 
 * Allows users to create and manage their own custom color themes.
 * Users can duplicate system themes or create themes from scratch.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Grid,
  FormField,
  Select,
  Alert,
  Spinner,
  Badge,
  ButtonDropdown,
} from '@cloudscape-design/components';
import {
  listUserThemes,
  deleteUserTheme,
  listProducts,
} from '../../services/themeApi';
import {
  ColorTheme,
  DataProduct,
} from '../../types/themes';
import ThemeEditor from '../admin/ThemeEditor';

const UserThemeCreator: React.FC = () => {
  const [products, setProducts] = useState<DataProduct[]>([]);
  const [userThemes, setUserThemes] = useState<ColorTheme[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTheme, setEditingTheme] = useState<ColorTheme | null>(null);
  const [duplicatingTheme, setDuplicatingTheme] = useState<ColorTheme | null>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct && selectedAttribute) {
      loadUserThemes();
    }
  }, [selectedProduct, selectedAttribute]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await listProducts();
      setProducts(data.filter(p => p.is_active));
      
      // Select first product by default
      if (data.length > 0) {
        setSelectedProduct(data[0].product_id);
        if (data[0].attributes.length > 0) {
          setSelectedAttribute(data[0].attributes[0].attribute_id);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadUserThemes = async () => {
    if (!selectedProduct || !selectedAttribute) return;

    try {
      setLoading(true);
      setError(null);
      const themes = await listUserThemes(selectedProduct, selectedAttribute);
      // Filter to only show user themes (not system themes)
      setUserThemes(themes.filter(t => t.theme_type === 'user'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTheme(null);
    setDuplicatingTheme(null);
    setShowEditor(true);
  };

  const handleEdit = (theme: ColorTheme) => {
    setEditingTheme(theme);
    setDuplicatingTheme(null);
    setShowEditor(true);
  };

  const handleDuplicate = (theme: ColorTheme) => {
    // Create a new theme object with the same mappings but no theme_id
    // This will be used to pre-populate the editor for a new theme
    setDuplicatingTheme(theme);
    setEditingTheme(null);
    setShowEditor(true);
  };

  const handleDelete = async (theme: ColorTheme) => {
    if (!confirm(`Are you sure you want to delete "${theme.theme_name}"?`)) {
      return;
    }

    try {
      await deleteUserTheme(theme.theme_id);
      await loadUserThemes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete theme');
    }
  };

  const handleEditorClose = (saved: boolean) => {
    setShowEditor(false);
    setEditingTheme(null);
    setDuplicatingTheme(null);
    
    if (saved) {
      loadUserThemes();
    }
  };

  const selectedProductData = products.find(p => p.product_id === selectedProduct);
  const selectedAttributeData = selectedProductData?.attributes.find(
    a => a.attribute_id === selectedAttribute
  );

  if (loading && products.length === 0) {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
          <Spinner size="large" />
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            description="Create and manage your own custom color themes. You can create themes from scratch or duplicate existing system themes as a starting point."
          >
            My Custom Themes
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Alert type="info">
            Theme changes may take up to 60 seconds to appear on connected ESP32 devices due to caching.
          </Alert>
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <SpaceBetween size="l">
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField label="Data Product">
              <Select
                selectedOption={
                  selectedProduct
                    ? { label: products.find(p => p.product_id === selectedProduct)?.display_name || '', value: selectedProduct }
                    : null
                }
                onChange={(e) => {
                  setSelectedProduct(e.detail.selectedOption.value || '');
                  const product = products.find(p => p.product_id === e.detail.selectedOption.value);
                  if (product && product.attributes.length > 0) {
                    setSelectedAttribute(product.attributes[0].attribute_id);
                  }
                }}
                options={products.map((product) => ({
                  label: product.display_name,
                  value: product.product_id
                }))}
                placeholder="Select a product"
              />
            </FormField>

            <FormField label="Attribute">
              <Select
                selectedOption={
                  selectedAttribute && selectedProductData
                    ? {
                        label: `${selectedProductData.attributes.find(a => a.attribute_id === selectedAttribute)?.display_name || ''}${selectedAttributeData?.unit ? ` (${selectedAttributeData.unit})` : ''}`,
                        value: selectedAttribute
                      }
                    : null
                }
                onChange={(e) => setSelectedAttribute(e.detail.selectedOption.value || '')}
                options={selectedProductData?.attributes.filter(a => a.is_active).map((attr) => ({
                  label: `${attr.display_name}${attr.unit ? ` (${attr.unit})` : ''}`,
                  value: attr.attribute_id
                })) || []}
                placeholder="Select an attribute"
                disabled={!selectedProduct}
              />
            </FormField>
          </Grid>
        </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                variant="primary"
                onClick={handleCreateNew}
                disabled={!selectedProduct || !selectedAttribute}
                iconName="add-plus"
              >
                Create New Theme
              </Button>
            }
          >
            Your Themes for {selectedAttributeData?.display_name || 'Selected Attribute'}
          </Header>
        }
      >
        {loading ? (
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
              Loading themes...
            </Box>
          </Box>
        ) : userThemes.length === 0 ? (
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Box variant="p" color="text-body-secondary" margin={{ bottom: 's' }}>
              You haven't created any custom themes for this attribute yet.
            </Box>
            <Button
              variant="primary"
              onClick={handleCreateNew}
              iconName="add-plus"
            >
              Create Your First Theme
            </Button>
          </Box>
        ) : (
          <SpaceBetween size="m">
            {userThemes.map((theme) => (
              <Container key={theme.theme_id}>
                <SpaceBetween size="m">
                  <Box>
                    <SpaceBetween direction="horizontal" size="xs">
                      <Box variant="h3">{theme.theme_name}</Box>
                      <Badge color="blue">
                        {theme.mapping_type === 'categorical' ? 'Categorical' : 'Range-based'}
                      </Badge>
                      <Badge>
                        {theme.mappings.length} mapping{theme.mappings.length !== 1 ? 's' : ''}
                      </Badge>
                      <Badge color="grey">v{theme.version}</Badge>
                    </SpaceBetween>
                  </Box>

                  <Box>
                    <SpaceBetween direction="horizontal" size="s">
                      {theme.mappings.map((mapping, index) => (
                        <Box key={index}>
                          <SpaceBetween direction="horizontal" size="xs">
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                backgroundColor: mapping.color
                              }}
                            />
                            <Box fontSize="body-s">{mapping.label}</Box>
                          </SpaceBetween>
                        </Box>
                      ))}
                    </SpaceBetween>
                  </Box>

                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        onClick={() => handleEdit(theme)}
                        iconName="edit"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDuplicate(theme)}
                        iconName="copy"
                      >
                        Duplicate
                      </Button>
                      <Button
                        onClick={() => handleDelete(theme)}
                        iconName="remove"
                      >
                        Delete
                      </Button>
                    </SpaceBetween>
                  </Box>
                </SpaceBetween>
              </Container>
            ))}
          </SpaceBetween>
        )}
      </Container>

      {showEditor && (
        <ThemeEditor
          theme={editingTheme || duplicatingTheme}
          productId={duplicatingTheme?.data_product || selectedProduct}
          attributeId={duplicatingTheme?.attribute || selectedAttribute}
          products={products}
          isSystemTheme={false}
          isDuplicating={!!duplicatingTheme}
          onClose={handleEditorClose}
        />
      )}
    </SpaceBetween>
  );
};

export default UserThemeCreator;
